import {CSSProperties} from "react";
import {useApp} from "../app.context";

// Either flat options or grouped (with section headers via <optgroup>).
// Mutually exclusive — pass one, not both.
export interface SelectableGroup<T> {
    label: string;
    options: T[];
}

export interface SelectableProps<T> {
    value: T;
    options?: T[];
    groups?: SelectableGroup<T>[];
    onChange(value: T): void;
    style?: CSSProperties;
    // Render option values as-is instead of treating them as localization
    // keys. Use for values that aren't loc keys (branch names, enum values)
    // — otherwise they render as missing-key markers ([[...]]).
    literalOptions?: boolean;
    // When set, a disabled placeholder option represents the empty state. Without
    // it an unset (value '') picker shows the first real option, so re-selecting
    // that option fires no onChange — the bug where you have to pick another and
    // come back. The placeholder makes "nothing chosen" the visible default, so
    // the first real pick is a genuine change.
    placeholder?: string;
}

function Selectable<T extends string | number>(props: SelectableProps<T>) {
    const {getLocalizationValue} = useApp();
    const label = (id: T) => props.literalOptions ? id.toString() : getLocalizationValue(id.toString());

    return (
        <select style={{...{width: '200px'}, ...props.style}} defaultValue={props.value} onChange={(v) => {
            props.onChange(v.target.value as T);
        }}>
            {props.placeholder !== undefined &&
                <option value={''} disabled hidden>{props.placeholder}</option>}
            {props.groups
                ? props.groups.map((group, gi) => (
                    <optgroup key={gi} label={group.label}>
                        {group.options.map((id, index) => (
                            <option key={index} value={id}>{label(id)}</option>
                        ))}
                    </optgroup>
                ))
                : (props.options ?? []).map((id, index) => (
                    <option key={index} value={id}>{label(id)}</option>
                ))}
        </select>
    )
}

export default Selectable;